import React, { useMemo } from "react";

import { DropdownMulti } from "oolib";

import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { PRIORITIES_TABLE_COLUMN_OPTIONS } from "@/components/analysisModules/TopicClusteringTreeViewV2/PrioritiesTableV2";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getTplsWithAnnosEnabled } from "@/components/analysisModules/AnnotationExplorer/useGetAnnotationExplorerData";

import { ConfigForm_SectionWrapper } from "../../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { SwitchRow } from "../../../utils/CombineSegmentsControl";
import { CollapseTransition } from "../../../utils/CollapseTransition";
import { WidgetFilters } from "../../../utils/WidgetFilters";
import { useWidgetFilterControls } from "../../../utils/useWidgetFilterControls";

import { DEFAULTS, GRAIN_OPTIONS, LAYOUT_OPTIONS, TABLE_TAB_OPTIONS, TREND_WINDOW_OPTIONS, periodPickerOptions, pickerValue, resolvePeriod } from "./settings";

/**
 * The Priorities widget's config: what to score (sub-topics, topics, or let
 * the viewer switch), how to draw it (bubble chart or table), which period,
 * an on/off per section, and the widget's own filters. A cleared picker
 * returns to the default rather than leaving the widget without a value.
 *
 * Filters are the shared WidgetFilters over every annotation-enabled
 * template — Priorities scores annotations, so the same filters the
 * Annotation Explorer widget offers apply. They live at
 * `<propsPath>.widgetFilters`, the same key every other widget uses.
 *
 * The column picker offers whatever the table declares
 * (PRIORITIES_TABLE_COLUMN_OPTIONS, derived from its COLUMN_SPECS), so a
 * column added to the table appears here on its own. It reads as "which
 * columns to show" but stores the complement — the HIDDEN ids — so a new
 * column shows up on widgets saved before it existed.
 */
export const PrioritiesWidgetConfigForm = ({ props, updateProps, propsPath, widgetConfig, setConfig, underEditWidgetIdx }) => {
  const settings = { ...DEFAULTS, ...props };
  const allTpls = useGetQueryData("allTpls") || [];
  const annoTpls = useMemo(() => getTplsWithAnnosEnabled({ allTpls }), [allTpls]);
  const { filterHeaderProps, filterControlProps } = useWidgetFilterControls({ hasDimensions: annoTpls.length > 0 });
  const set = (key) => (value) => updateProps((p) => ({ ...p, [key]: value }));
  const pick = (key) => (_id, option) => set(key)(option?.value ?? DEFAULTS[key]);

  const { data: dateFilterOptions } = useGetDatesThatMatterFilterOptions();

  const hiddenColumns = settings.hiddenColumns || [];
  const shownColumns = useMemo(
    () => PRIORITIES_TABLE_COLUMN_OPTIONS.filter((c) => !hiddenColumns.includes(c.value)).map((c) => c.value),
    [hiddenColumns]
  );
  const handleColumnsChange = (_id, selected) => {
    const shown = new Set((selected || []).map((v) => v?.value ?? v));
    set("hiddenColumns")(PRIORITIES_TABLE_COLUMN_OPTIONS.filter((c) => !shown.has(c.value)).map((c) => c.value));
  };
  const periodOptions = useMemo(() => periodPickerOptions(dateFilterOptions?.periods), [dateFilterOptions]);
  // The "Latest month" card names the month it currently resolves to.
  const latest = resolvePeriod(dateFilterOptions?.periods, DEFAULTS.periodValue);
  const periodValue = pickerValue(periodOptions, settings.periodValue, "Period");
  const periodCard =
    periodValue && settings.periodValue === DEFAULTS.periodValue && latest
      ? { ...periodValue, display: `Latest month — ${latest.display}` }
      : periodValue;

  return (
    <>
      <ConfigForm_SectionWrapper icon="Eye" title="View">
        <DataFieldInputSelector
          id="scoreGrain"
          options={GRAIN_OPTIONS}
          value={pickerValue(GRAIN_OPTIONS, settings.scoreGrain, "Scores")}
          onChange={pick("scoreGrain")}
          placeholder="What to score"
          placeholderIcon="Eye"
          trailingIcon="CaretRight"
          hideSearchHeader
        />
        <DataFieldInputSelector
          id="layout"
          options={LAYOUT_OPTIONS}
          value={pickerValue(LAYOUT_OPTIONS, settings.layout, "Layout")}
          onChange={pick("layout")}
          placeholder="How to draw it"
          placeholderIcon="Table"
          trailingIcon="CaretRight"
          hideSearchHeader
        />
        <CollapseTransition isVisible={settings.layout === "table"}>
          <DataFieldInputSelector
            id="tableTab"
            options={TABLE_TAB_OPTIONS}
            value={pickerValue(TABLE_TAB_OPTIONS, settings.tableTab, "Table shows")}
            onChange={pick("tableTab")}
            placeholder="Table shows"
            placeholderIcon="Table"
            trailingIcon="CaretRight"
            hideSearchHeader
          />
        </CollapseTransition>
      </ConfigForm_SectionWrapper>

      <ConfigForm_SectionWrapper icon="CalendarBlank" title="Period">
        <DataFieldInputSelector
          id="periodValue"
          options={periodOptions}
          value={periodCard}
          onChange={pick("periodValue")}
          placeholder="Period"
          placeholderIcon="CalendarBlank"
          trailingIcon="CaretRight"
        />
        <DataFieldInputSelector
          id="trendWindow"
          options={TREND_WINDOW_OPTIONS}
          value={pickerValue(TREND_WINDOW_OPTIONS, settings.trendWindow, "Trend over")}
          onChange={pick("trendWindow")}
          placeholder="Trend over"
          placeholderIcon="CalendarBlank"
          trailingIcon="CaretRight"
          hideSearchHeader
        />
      </ConfigForm_SectionWrapper>

      {/* Only the table has columns; the field has axes. */}
      <CollapseTransition isVisible={settings.layout === "table"}>
        <ConfigForm_SectionWrapper icon="Table" title="Columns">
          <DropdownMulti
            id="shownColumns"
            options={PRIORITIES_TABLE_COLUMN_OPTIONS}
            value={shownColumns}
            onChange={handleColumnsChange}
            saveValueAsString
            placeholder="Columns to show"
            showSelectAll
            popOutOfOverflowHiddenParent
          />
        </ConfigForm_SectionWrapper>
      </CollapseTransition>

      <ConfigForm_SectionWrapper icon="SlidersHorizontal" title="Sections">
        <SwitchRow id="showTrendWindow" label="Trend window (2 / 3 / 6 / 12 periods)" value={settings.showTrendWindow} onChange={set("showTrendWindow")} />
        <SwitchRow id="showZoneBar" label="Zone filter" value={settings.showZoneBar} onChange={set("showZoneBar")} />
        <CollapseTransition isVisible={settings.layout === "field"}>
          <SwitchRow id="showActFirst" label="Act-first list" value={settings.showActFirst} onChange={set("showActFirst")} />
        </CollapseTransition>
      </ConfigForm_SectionWrapper>

      <ConfigForm_SectionWrapper icon="Funnel" title="Filters" {...filterHeaderProps}>
        <WidgetFilters
          widgetConfig={widgetConfig}
          updateWidgetConfig={setConfig}
          widgetIndex={underEditWidgetIdx}
          selectedTpls={annoTpls}
          configPath={`${propsPath}.widgetFilters`}
          {...filterControlProps}
        />
      </ConfigForm_SectionWrapper>
    </>
  );
};
